import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/useAuth';
import { isSubscribeEnabled } from '../config/features';
import { SUBSCRIPTIONS_CLOSED_MESSAGE } from '../config/subscriptionCopy';

const MS_PER_DAY = 1000 * 60 * 60 * 24;

/**
 * Slim banner shown while the active business is on its free trial ("trialing") or after it
 * has lapsed ("trialended"). While trialing-without-a-subscription it counts down; once subscribed
 * (subscriptionId present) it shows an info message; once ended it says the trial is over.
 *
 * The Subscribe CTA only appears while subscribing is open (isSubscribeEnabled). With it closed
 * there is nowhere useful to send the user, so the banner says why instead of offering a dead
 * button — see SUBSCRIPTIONS_CLOSED_MESSAGE.
 */
const FreeTrialBanner: React.FC = () => {
  const { activeBusiness } = useAuth();
  const navigate = useNavigate();

  const status = activeBusiness?.subscriptionStatus;
  if (status !== 'trialing' && status !== 'trialended') return null;

  const subscribed = !!activeBusiness?.subscriptionId;
  const end = activeBusiness?.subscriptionEndDate ? new Date(activeBusiness.subscriptionEndDate) : null;
  const endValid = end && !isNaN(end.getTime());
  const daysLeft = endValid ? Math.ceil((end!.getTime() - Date.now()) / MS_PER_DAY) : 0;

  // Subscribed and still in the trial window: informational only, no CTA.
  if (status === 'trialing' && subscribed) {
    const chargeOn = endValid ? end!.toLocaleDateString() : 'your trial end date';
    return (
      <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 dark:border-amber-500/20 dark:bg-amber-500/10">
        <div className="mx-auto flex max-w-(--breakpoint-2xl) flex-wrap items-center gap-3">
          <span className="text-sm text-amber-800 dark:text-amber-300">
            Free trial — first charge on {chargeOn}.
          </span>
        </div>
      </div>
    );
  }

  const ended = status === 'trialended' || daysLeft <= 0;
  const message = ended
    ? 'Your free trial has ended'
    : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left on your free trial`;

  return (
    <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 dark:border-amber-500/20 dark:bg-amber-500/10">
      <div className="mx-auto flex max-w-(--breakpoint-2xl) flex-wrap items-center justify-between gap-3">
        <span className="text-sm text-amber-800 dark:text-amber-300">{message}</span>
        {isSubscribeEnabled() ? (
          <button
            type="button"
            onClick={() => navigate('/billing')}
            className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white shadow-sm hover:bg-amber-700"
          >
            Subscribe
          </button>
        ) : (
          <span className="text-sm text-amber-800/80 dark:text-amber-300/80">{SUBSCRIPTIONS_CLOSED_MESSAGE}</span>
        )}
      </div>
    </div>
  );
};

export default FreeTrialBanner;
